import { useCallback, useEffect, useRef, useState } from "react";
import type { ChatAttachmentItem } from "./useSessionAttachments";

type PendingSend = { text: string; sessionId: string };

/**
 * Last-resort release, NOT the primary failure signal — a failed analysis now
 * reaches a terminal "error" state of its own, which frees the queue immediately.
 * This only covers a document that somehow reaches neither terminal state.
 *
 * It has to outlast the slowest legitimate PDF analysis, which is the only thing
 * that waits here: a video is ready as soon as it has finished uploading, because
 * nothing reads it until the agent asks.
 */
const MAX_QUEUE_WAIT_MS = 960_000;

/**
 * Lets the user attach a file, type a message, and hit send in one motion.
 *
 * A PDF is parsed and embedded by a lambda, and only becomes readable by the chat
 * agent (via QueryDocuments) once its state flips to "completed". If the user
 * sends while one is still uploading/processing, the agent would answer blind.
 * Instead of blocking the composer, this hook holds the message and fires it the
 * moment analysis settles — so "send now" always waits for the file just attached.
 *
 * "Settles" means TERMINAL, not successful: a failed attachment releases the
 * message too, because there is nothing left to wait for and the agent is told
 * to say the file could not be read.
 */
export function useDeferredSend({
  attachments,
  sessionId,
  onSend,
}: {
  attachments: ChatAttachmentItem[];
  sessionId: string | null;
  onSend: (text: string) => void;
}) {
  // Keep the latest onSend without making the flush effect depend on its
  // identity — the effect should only re-run when the attachment/session state
  // that gates the send actually changes. This effect is declared before the
  // flush effect so the ref is current by the time the flush reads it.
  const onSendRef = useRef(onSend);
  useEffect(() => {
    onSendRef.current = onSend;
  }, [onSend]);

  const [pendingSend, setPendingSend] = useState<PendingSend | null>(null);

  const attachmentsPending = attachments.some(
    (a) => a.state === "uploading" || a.state === "processing",
  );

  // Reacts to the attachment-analysis query settling (an external, polled
  // system): once the just-attached file finishes, fire the held message. If the
  // user has since switched chats, drop it instead so it can never land in the
  // wrong conversation. Clearing the one-shot queue here is the effect's purpose.
  useEffect(() => {
    if (!pendingSend) return;
    const sessionMatches = pendingSend.sessionId === sessionId;
    if (sessionMatches && attachmentsPending) return; // still analyzing — wait
    const shouldSend = sessionMatches && !attachmentsPending;
    const { text } = pendingSend;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot flush of a queue driven by external async completion
    setPendingSend(null);
    if (shouldSend) onSendRef.current(text);
  }, [pendingSend, attachmentsPending, sessionId]);

  // Bounded wait: if analysis never settles, don't let the message hang forever.
  useEffect(() => {
    if (!pendingSend) return;
    const timer = setTimeout(() => {
      if (pendingSend.sessionId === sessionId) onSendRef.current(pendingSend.text);
      setPendingSend(null);
    }, MAX_QUEUE_WAIT_MS);
    return () => clearTimeout(timer);
  }, [pendingSend, sessionId]);

  const queueOrSend = useCallback(
    (text: string) => {
      if (attachmentsPending && sessionId) {
        setPendingSend({ text, sessionId });
      } else {
        onSendRef.current(text);
      }
    },
    [attachmentsPending, sessionId],
  );

  return {
    /** True while any attachment on this session is still uploading/analyzing. */
    attachmentsPending,
    /** True while a message is held, waiting on attachment analysis. */
    isQueued: pendingSend !== null,
    /** Send now, or hold until pending attachments finish, then auto-send. */
    queueOrSend,
  };
}
